import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

/**
 * BasicProductDetails — universal "always show" detail fields for ANY physical
 * product (merch, collectible, apparel, etc.). Mirrors the level of depth
 * mainstream e-commerce marketplaces expect: brand, condition, colour, size,
 * material, weight, dimensions, warranty, care, tags, country of origin.
 *
 * All values are stored on the product's `attributes` object so they render on
 * the public product page.
 */
const COLORS = ["Black", "White", "Grey", "Red", "Pink", "Orange", "Yellow", "Green", "Blue", "Purple", "Brown", "Beige", "Gold", "Silver", "Multi"];
const SIZES_STD = ["XS", "S", "M", "L", "XL", "XXL", "3XL", "One Size"];
const CONDITIONS = ["Brand New", "Like New", "Refurbished", "Used - Excellent", "Used - Good", "Used - Fair"];

export default function BasicProductDetails({ attributes = {}, onAttributesChange }) {
  const set = (k, v) => onAttributesChange({ ...attributes, [k]: v });

  const toggleChip = (key, opt) => {
    const cur = Array.isArray(attributes[key]) ? attributes[key] : [];
    set(key, cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt]);
  };

  const isChipSelected = (key, opt) => Array.isArray(attributes[key]) && attributes[key].includes(opt);

  return (
    <div className="space-y-4 border-t border-[#1A2030] pt-4" data-testid="basic-product-details">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Product details</span>
        <span className="text-[10px] text-[#6A7280]">Buyers see all of these on the product page</span>
      </div>

      {/* Row 1 — brand, condition */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-white text-sm">Brand</Label>
          <Input
            value={attributes.brand || ""}
            onChange={(e) => set("brand", e.target.value)}
            placeholder="e.g. StardomFans Studios"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-brand"
          />
        </div>
        <div>
          <Label className="text-white text-sm">Condition</Label>
          <Select value={attributes.condition || "Brand New"} onValueChange={(v) => set("condition", v)}>
            <SelectTrigger className="bg-[#050810] border-[#1A2030] text-white" data-testid="basic-condition">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#0A0E18] border-[#1A2030]">
              {CONDITIONS.map((c) => <SelectItem key={c} value={c} className="text-white">{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Row 2 — colours */}
      <div>
        <Label className="text-white text-sm">Colours available</Label>
        <div className="flex flex-wrap gap-2 mt-1" data-testid="basic-colors">
          {COLORS.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => toggleChip("colors", c)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                isChipSelected("colors", c)
                  ? "bg-[#EC008C] text-black border-[#EC008C]"
                  : "bg-transparent text-[#8A92A0] border-[#1A2030] hover:border-[#EC008C]"
              }`}
              data-testid={`basic-color-${c.toLowerCase()}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {/* Row 3 — sizes */}
      <div>
        <Label className="text-white text-sm">Sizes available</Label>
        <div className="flex flex-wrap gap-2 mt-1" data-testid="basic-sizes">
          {SIZES_STD.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => toggleChip("sizes", s)}
              className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all ${
                isChipSelected("sizes", s)
                  ? "bg-[#EC008C] text-black border-[#EC008C]"
                  : "bg-transparent text-[#8A92A0] border-[#1A2030] hover:border-[#EC008C]"
              }`}
              data-testid={`basic-size-${s.toLowerCase()}`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Row 4 — material, country of origin */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-white text-sm">Material / Fabric</Label>
          <Input
            value={attributes.material || ""}
            onChange={(e) => set("material", e.target.value)}
            placeholder="e.g. 100% organic cotton"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-material"
          />
        </div>
        <div>
          <Label className="text-white text-sm">Country of origin</Label>
          <Input
            value={attributes.country_of_origin || ""}
            onChange={(e) => set("country_of_origin", e.target.value)}
            placeholder="e.g. Made in Portugal"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-country"
          />
        </div>
      </div>

      {/* Row 5 — weight + dimensions (shipping-critical) */}
      <div>
        <Label className="text-white text-sm">Weight &amp; dimensions <span className="text-[#6A7280] text-xs font-normal">(used for shipping quotes)</span></Label>
        <div className="grid grid-cols-4 gap-2">
          <Input
            type="number"
            step="0.01"
            value={attributes.weight_g || ""}
            onChange={(e) => set("weight_g", e.target.value)}
            placeholder="Weight (g)"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-weight"
          />
          <Input
            type="number"
            step="0.1"
            value={attributes.length_cm || ""}
            onChange={(e) => set("length_cm", e.target.value)}
            placeholder="L (cm)"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-length"
          />
          <Input
            type="number"
            step="0.1"
            value={attributes.width_cm || ""}
            onChange={(e) => set("width_cm", e.target.value)}
            placeholder="W (cm)"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-width"
          />
          <Input
            type="number"
            step="0.1"
            value={attributes.height_cm || ""}
            onChange={(e) => set("height_cm", e.target.value)}
            placeholder="H (cm)"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-height"
          />
        </div>
      </div>

      {/* Row 6 — care */}
      <div>
        <Label className="text-white text-sm">Care instructions <span className="text-[#6A7280] text-xs font-normal">(optional)</span></Label>
        <Textarea
          value={attributes.care_instructions || ""}
          onChange={(e) => set("care_instructions", e.target.value)}
          rows={2}
          placeholder="e.g. Machine wash cold, tumble dry low, do not iron print"
          className="bg-[#050810] border-[#1A2030] text-white"
          data-testid="basic-care"
        />
      </div>

      {/* Row 7 — warranty */}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-white text-sm">Warranty (months) <span className="text-[#6A7280] text-xs font-normal">(optional)</span></Label>
          <Input
            type="number"
            value={attributes.warranty_months || ""}
            onChange={(e) => set("warranty_months", e.target.value)}
            placeholder="e.g. 12"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-warranty"
          />
        </div>
        <div>
          <Label className="text-white text-sm">SKU / Model # <span className="text-[#6A7280] text-xs font-normal">(optional)</span></Label>
          <Input
            value={attributes.sku || ""}
            onChange={(e) => set("sku", e.target.value)}
            placeholder="e.g. SF-CAP-001"
            className="bg-[#050810] border-[#1A2030] text-white"
            data-testid="basic-sku"
          />
        </div>
      </div>
    </div>
  );
}
